
import { useAdminAuth } from "../../hooks/useAdminAuth";
import { useState, useEffect } from "react";
import { NavLink, Routes, Route, Outlet, useLocation, Navigate } from 'react-router-dom'
import Kruzky from "./components/Kruzky";
import Vseobecne from "./components/Vseobecne";
import UserManagement from "./components/UserManagement";
import Prihlasky from "./components/Prihlasky";
import SystemManagement from "./components/SystemManagement";
import { useLogout } from "../../hooks/useLogout";
import { authenticatedFetch } from "../../utils/auth";
import { IconChevronDown, IconUsers } from "@tabler/icons-react";

const getKruzky = async () => {
    const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/kruzok/all`);
    if (!response.ok) {
        throw new Error("Failed to fetch kruzky");
    }
    const data = await response.json();
    const res: Kruzok[] = data.data

    return res;
}

export const getYearTypeName = (type: schoolyeartype) => {
    switch (type) {
        case 'PRESCHOOL':
            return "Materská škola";
        case 'PRIMARY':
            return "Základná škola";
        case 'HIGHSCHOOL':
            return "Stredná škola";
        case 'UNIVERSITY':
            return "VŠ/Mladý pracujúci";
        default:
            return "Neznámy typ";
    }
}

export interface Kruzok {
    id: string;
    name: string;
    description?: string;
    price: number;
    capacity: number;
    time: string;
    type: string; // Assuming type is a string, adjust if it's an enum or another type
    gender: 'MALE' | 'FEMALE' | 'BOTH';
    isStretko?: boolean;
    requiresZapisne?: boolean;
    schoolYear?: Rocnik[];
    currentRegistrations?: number;
    applicants?: { id: string; firstName: string; lastName: string }[];
}

export type schoolyeartype = 'PRIMARY' | 'HIGHSCHOOL' | 'PRESCHOOL' | 'UNIVERSITY';

export interface Rocnik {
    id: string;
    name: string;
    eduName?: string;
    order: number;
    type: schoolyeartype;
}


const Admin = () => {
    const [kruzky, setKruzky] = useState<Kruzok[]>([]);
    const [rocniky, setRocniky] = useState<Rocnik[]>([]);
    const { hasAdminAccess, isViewer } = useAdminAuth();
    const { logout } = useLogout();

    const location = useLocation();

    // Update page title based on current route
    useEffect(() => {
        const path = location.pathname.split('/').pop();
        const sectionTitles: Record<string, string> = {
            'kruzky': 'Krúžky',
            'vseob': 'Všeobecné',
            'prihlasky': 'Prihlášky',
            'user': 'Používatelia',
            'system': 'Systém'
        };
        const title = sectionTitles[path || 'kruzky'] || 'Administrácia';
        document.title = `Admin | ${title} - SCVČ Tri Hôrky`;

        return () => { document.title = 'SCVČ Tri Hôrky'; };
    }, [location.pathname]);

    const handleLogout = () => { logout(); };

    useEffect(() => {
        // Fetch shared admin data on mount when admin access exists
        if (!hasAdminAccess || isViewer) return;

        const fetchAll = async () => {
            try {
                const [kruzkyData, rocnikResp] = await Promise.all([
                    getKruzky(),
                    authenticatedFetch(`${process.env.REACT_APP_PATH}/api/vseob/rocnik`)
                ]);

                setKruzky(kruzkyData || []);

                if (rocnikResp && rocnikResp.ok) {
                    const rdata = await rocnikResp.json();
                    setRocniky(rdata.data || []);
                }
            } catch (err) {
                console.error('Error fetching admin shared data', err);
            }
        };

        fetchAll();
    }, [hasAdminAccess, isViewer]);

    if (isViewer) {
        return <BernolakovaView onLogout={handleLogout} />;
    }

    return (
        <div className="min-h-screen bg-gray-50 ">
            {/* Header */}
            <div className="bg-white shadow-sm border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
                    <div className="flex justify-between items-center h-14 sm:h-16">
                        <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Administrácia</h1>
                        <div className="flex items-center space-x-2 sm:space-x-4">
                            <span className="text-xs sm:text-sm text-gray-600 hidden sm:block">Prihlásený ako admin</span>
                            <button onClick={handleLogout} className="text-primary hover:text-red-700 text-xs sm:text-sm font-medium px-2 py-1 rounded">
                                Odhlásiť sa
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <div className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
                    <nav className="flex overflow-x-auto space-x-4 sm:space-x-8 scrollbar-hide">
                        {[
                            { to: 'kruzky', label: 'Krúžky' },
                            { to: 'vseob', label: 'Všeobecné' },
                            { to: 'prihlasky', label: 'Prihlášky' },
                            { to: 'user', label: 'Používatelia' },
                            { to: 'system', label: 'Systém' }
                        ].map((tab) => (
                            <NavLink
                                key={tab.to}
                                to={tab.to}
                                className={({ isActive }) => `py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors duration-200 whitespace-nowrap flex-shrink-0 ${isActive ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                            >
                                {tab.label}
                            </NavLink>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Main Content - nested routes will render here */}
            <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
                <div className="space-y-6 ">
                    <Routes>
                        <Route index element={<Navigate to="kruzky" replace />} />
                        <Route path="kruzky" element={<Kruzky kruzky={kruzky} setKruzky={setKruzky} />} />
                        <Route path="vseob" element={<Vseobecne rocnik={rocniky} setRocnik={setRocniky} />} />
                        <Route path="prihlasky" element={<Prihlasky />} />
                        <Route path="user" element={<UserManagement />} />
                        <Route path="system" element={<SystemManagement />} />
                    </Routes>
                    {/* Outlet in case other nested child routes are added elsewhere */}
                    <Outlet />
                </div>

            </div>


        </div>
    );
}

export default Admin;

const BernolakovaView = ({ onLogout }: { onLogout: () => void }) => {
    const [kruzky, setKruzky] = useState<Kruzok[]>([]);
    const [expandedKruzokId, setExpandedKruzokId] = useState<string | null>(null);
    const [expandedMobileId, setExpandedMobileId] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        authenticatedFetch(`${process.env.REACT_APP_PATH}/api/admin/bernolakova-kruzky`)
            .then(response => {
                if (!response.ok) throw new Error('Krúžky sa nepodarilo načítať.');
                return response.json();
            })
            .then(data => setKruzky((data.data as Kruzok[]).sort((a, b) => a.name.localeCompare(b.name, 'sk'))))
            .catch(() => setError('Krúžky sa nepodarilo načítať.'))
            .finally(() => setLoading(false));
    }, []);

    const studentList = (kruzok: Kruzok) => <div className="border-t border-gray-100 px-4 sm:px-5 py-3 max-h-64 overflow-y-auto">
        {kruzok.applicants?.length ? <ul className="divide-y divide-gray-100 text-sm text-gray-900">
            {kruzok.applicants.map(person => <li key={person.id} className="py-2 first:pt-0 last:pb-0">{person.firstName} {person.lastName}</li>)}
        </ul> : <p className="text-sm text-gray-500">Zatiaľ nikto.</p>}
    </div>;

    const studentToggle = (kruzok: Kruzok, expanded: boolean, onClick: () => void, visibility: string) => <button
        type="button"
        aria-expanded={expanded}
        onClick={onClick}
        className={`${visibility} w-full items-center justify-between gap-2 px-4 sm:px-5 py-3 text-sm font-medium text-primary hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary`}
    >
        <span>Prihlásení ({kruzok.applicants?.length ?? 0})</span>
        <IconChevronDown size={18} aria-hidden="true" className={`shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
    </button>;

    return <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-4">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Administrácia</h1>
                <div className="flex items-center gap-2 sm:gap-4">
                    <span className="hidden sm:block text-sm text-gray-600">Bernolákova · iba čítanie</span>
                    <button onClick={onLogout} className="text-primary hover:text-red-700 text-xs sm:text-sm font-medium px-2 py-1 rounded">Odhlásiť sa</button>
                </div>
            </div>
        </header>
        <div className="bg-white shadow-sm">
            <nav aria-label="Administrácia" className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
                <span className="inline-block py-3 sm:py-4 px-2 sm:px-1 border-b-2 border-primary text-primary font-medium text-xs sm:text-sm">Krúžky</span>
            </nav>
        </div>
        <main className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4 sm:mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Krúžky Bernolákova</h2>
                {!loading && !error && <span className="text-sm text-gray-600">{kruzky.length} krúžkov</span>}
            </div>
            {error && <p role="alert" className="text-red-700">{error}</p>}
            {loading && <p className="text-gray-600">Načítavam krúžky...</p>}
            {!loading && !error && kruzky.length === 0 && <p className="text-gray-600">Žiadne krúžky.</p>}
            <div className="grid gap-4">
                {Array.from({ length: Math.ceil(kruzky.length / 2) }, (_, index) => kruzky.slice(index * 2, index * 2 + 2)).map(row => <div key={row[0].id} className="grid gap-4 lg:grid-cols-2">
                    {row.map(kruzok => <section key={kruzok.id} className="bg-white rounded-md border border-gray-200 shadow-sm overflow-hidden min-w-0">
                    <div className="px-4 sm:px-5 py-4 border-b border-gray-100">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                            <h3 className="text-base font-semibold text-gray-900 min-w-0 break-words">{kruzok.name}</h3>
                            <span className="shrink-0 inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800">
                                <IconUsers size={16} aria-hidden="true" />{kruzok.currentRegistrations ?? 0} prihlásených
                            </span>
                        </div>
                        {kruzok.description && <p className="text-sm text-gray-600 mt-2">{kruzok.description}</p>}
                    </div>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-3 px-4 sm:px-5 py-4 text-sm">
                        <div className="min-w-0"><dt className="text-gray-500">Čas</dt><dd className="font-medium text-gray-900 break-words">{kruzok.time}</dd></div>
                        <div className="min-w-0"><dt className="text-gray-500">Kapacita</dt><dd className="font-medium text-gray-900">{kruzok.capacity < 0 ? 'Neobmedzená' : kruzok.capacity}</dd></div>
                        <div className="min-w-0"><dt className="text-gray-500">Pohlavie</dt><dd className="font-medium text-gray-900">{{ MALE: 'Chlapci', FEMALE: 'Dievčatá', BOTH: 'Všetci' }[kruzok.gender]}</dd></div>
                        <div className="min-w-0"><dt className="text-gray-500">Ročníky</dt><dd className="font-medium text-gray-900 break-words">{kruzok.schoolYear?.map(year => year.name).join(', ') || '-'}</dd></div>
                    </dl>
                    <div className="border-t border-gray-200">
                        {studentToggle(kruzok, expandedMobileId === kruzok.id, () => setExpandedMobileId(expandedMobileId === kruzok.id ? null : kruzok.id), 'flex lg:hidden')}
                        {studentToggle(kruzok, row.some(item => item.id === expandedKruzokId), () => setExpandedKruzokId(row.some(item => item.id === expandedKruzokId) ? null : kruzok.id), 'hidden lg:flex')}
                        {expandedMobileId === kruzok.id && <div className="lg:hidden">{studentList(kruzok)}</div>}
                        {row.some(item => item.id === expandedKruzokId) && <div className="hidden lg:block">{studentList(kruzok)}</div>}
                    </div>
                    </section>)}
                </div>)}
            </div>
        </main>
    </div>;
};